import { isEmpty, isNil } from 'ramda';
import { COUNTRY_LIST, COUNTRY_NAME_ALIASES } from '../constants/country-list';

export const isNumber = value => !Number.isNaN(Number(value));

export const isDigitsOnly = str => /^\d+$/.test(String(str));

const STANDARD_LIMIT = 1000000;
/** used for compact localized representations, with 2 fraction digits when the value is larger than 1M.
 * This aims to give sense of magnitude, when the exact value will not make much difference.
 * It is also usable when you have limited space in the UI.
 * like this:
 * 3.23432 => 3.23
 * 1000 => 1,000
 * 110000 => 110,000
 * 980000 => 980,000
 * 1100000 => 1.1M
 * 1234000 => 1.23M
 * Using smaller than 1M values with this util, will not save the amount of chars (for many use-cases), that's why 1M is used
 * 934240 => 934.24K
 * */
export const defaultNumberFormat = (value, limit = STANDARD_LIMIT) =>
  isNumber(value)
    ? Number(value).toLocaleString(undefined, {
        notation: value >= limit ? 'compact' : 'standard',
        compactDisplay: 'short',
        maximumFractionDigits: 2
      })
    : value;

/** used for compact representations, with single fraction digit if needed, like this:
 * 1000 => 1K
 * 1010 => 1K
 * 1090 => 1.1K
 * 1200 => 1.2K
 * 1290 => 1.3K
 * 110000 => 110K
 * 1100000 => 1.1M
 * */
export const compactNumberFormat = value =>
  isNumber(value)
    ? (value < 1 && value > 0 && Number(value).toFixed(2)) ||
      Number(value).toLocaleString(undefined, { notation: 'compact', compactDisplay: 'short' })
    : value;

export const compactPreciseNumberFormat = (value, maximumFractionDigits = 1) =>
  isNumber(value)
    ? (value < 1 && value > 0 && Number(value).toFixed(2)) ||
      Number(value).toLocaleString(undefined, {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits
      })
    : value;

/** used for standard representations, without fraction digits (default Number.toLocaleString):
 * 3.23432 => 3.23
 * 1000 => 1,000
 * 104000 => 104,000
 * 9104000 => 9,104,000
 * For German it can be:
 * 30245.23432423 => 30.245,23
 */
export const standardNumberFormat = value =>
  isNumber(value) ? Number(value).toLocaleString(undefined, { notation: 'standard', maximumFractionDigits: 2 }) : value;
export const isNullOrEmpty = object => isNil(object) || isEmpty(object);
export const snakeCase = str =>
  str &&
  str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map(x => x.toLowerCase())
    .join('_');

export const hasContent = param => {
  if (param === null || param === undefined) {
    return false;
  }

  if (typeof param === 'string') {
    return param.trim().length > 0;
  }

  if (Array.isArray(param)) {
    return param.length > 0;
  }

  if (typeof param === 'object') {
    return Object.keys(param).length > 0;
  }

  return false;
};

const COUNTRY_CODE_BY_NAME = new Map([
  ...COUNTRY_LIST.map(({ country, code }) => [country, code]),
  ...Object.entries(COUNTRY_NAME_ALIASES)
]);

export const getCountryCode = country => COUNTRY_CODE_BY_NAME.get(country);

// Opt-out switch for silencing overlays (snackbars, intro bubbles, poppers) — used by e2e runs.
export const isSilenceToaster = () => localStorage.getItem('silenceToasters') === 'true';

export const safeJsonParse = value => {
  if (value && typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};
